import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { Receipt, SealCheck, Warning, CheckCircle, ArrowUp } from "@phosphor-icons/react";
import { fmtMoney } from "../quotes/PortalQuoteActions";

const BADGE = {
  under: { label: (d) => `Under-billed ${fmtMoney(Math.abs(d))}`, cls: "bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-200", Icon: Warning },
  over: { label: (d) => `Over-billed ${fmtMoney(Math.abs(d))}`, cls: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200", Icon: ArrowUp },
  matched: { label: () => "Matched", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200", Icon: CheckCircle },
  not_invoiced: { label: (d) => `Not invoiced yet — ${fmtMoney(Math.abs(d))} to bill`, cls: "bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-200", Icon: Warning },
};

/* Approved quote value vs invoiced total (ex VAT) — spot under-billing before the invoice goes out */
export default function ValueCheckCard({ jobId, refreshKey, className = "" }) {
  const [v, setV] = useState(null);
  useEffect(() => {
    API.get(`/jobs/${jobId}/value-check`).then(({ data }) => setV(data)).catch(() => setV(null));
  }, [jobId, refreshKey]);
  if (!v || v.status === "none") return null;
  const badge = BADGE[v.status];
  return (
    <div className={`rounded-lg border border-border bg-card px-4 py-3 flex items-center gap-4 flex-wrap ${className}`} data-testid="job-value-check">
      <div className="flex items-center gap-2 min-w-0">
        <Receipt size={18} className="text-primary shrink-0" />
        <div>
          <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Value check</p>
          <p className="text-[11px] text-muted-foreground">{v.sub_job_count > 0 ? `Main job + ${v.sub_job_count} sub-job${v.sub_job_count === 1 ? "" : "s"}` : "This job"} · ex VAT</p>
        </div>
      </div>
      <div className="flex items-center gap-5 flex-wrap ml-auto">
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"><SealCheck size={10} weight="fill" className="text-emerald-600" /> Approved</p>
          <p className="text-sm font-bold font-[Chivo] tabular-nums" data-testid="job-value-check-approved">{fmtMoney(v.approved_total)}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Invoiced{v.invoice_count ? ` (${v.invoice_count})` : ""}</p>
          <p className="text-sm font-bold font-[Chivo] tabular-nums" data-testid="job-value-check-invoiced">{fmtMoney(v.invoiced_total)}</p>
        </div>
        {badge && (
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${badge.cls}`} data-testid={`job-value-check-badge-${v.status}`}>
            <badge.Icon size={12} weight="fill" /> {badge.label(v.delta)}
          </span>
        )}
        {v.status !== "matched" && v.approved_total > 0 && (
          <Link to={`/invoices/new?job_id=${jobId}`} className="text-xs font-medium text-primary hover:underline" data-testid="job-value-check-invoice-link">Raise invoice →</Link>
        )}
      </div>
    </div>
  );
}
